import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  LayoutDashboard, Plus, ListOrdered, TrendingUp, Boxes, FileBarChart,
  ShieldCheck, Users as UsersIcon, ScrollText, Receipt, LogOut, Gem, CheckCircle2, CalendarDays, KeyRound, Smartphone, Bell,
} from "lucide-react";
import Assistant from "./Assistant";
import ChangePasswordModal from "./ChangePasswordModal";

const OWNER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/transactions", label: "Transaksi", icon: ListOrdered },
  { to: "/rates", label: "Rate Board", icon: TrendingUp },
  { to: "/products", label: "Produk", icon: Smartphone },
  { to: "/inventory", label: "Inventory", icon: Boxes },
  { to: "/approvals", label: "Approvals", icon: CheckCircle2 },
  { to: "/recap", label: "Daily Recap", icon: CalendarDays },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/expenses", label: "Expenses", icon: Receipt },
  { to: "/users", label: "Users", icon: UsersIcon },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
];

const OFFICER_NAV = [
  { to: "/home", label: "POS", icon: LayoutDashboard },
  { to: "/new", label: "Transaksi Baru", icon: Plus },
  { to: "/transactions", label: "Transaksi", icon: ListOrdered },
  { to: "/rates", label: "Rate", icon: TrendingUp },
  { to: "/inventory", label: "Inventory", icon: Boxes },
];

const MOBILE_OWNER = ["/dashboard", "/transactions", "/rates", "/inventory", "/reports"];
const MOBILE_OFFICER = ["/home", "/new", "/transactions", "/rates", "/inventory"];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [shift, setShift] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [pending, setPending] = useState(0);
  const items = user?.role === "owner" ? OWNER_NAV : OFFICER_NAV;
  const mobileSet = user?.role === "owner" ? MOBILE_OWNER : MOBILE_OFFICER;
  const mobileItems = items.filter((i) => mobileSet.includes(i.to));

  useEffect(() => {
    if (user?.role === "officer") {
      api.get("/shifts/current").then((r) => setShift(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (user?.role !== "owner") return;
    const load = () => api.get("/transactions/pending").then((r) => setPending(r.data.length)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user]);

  return (
    <div className="min-h-screen flex flex-col bg-[#0B0E14]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#222B3D] bg-[#0B0E14]/90 backdrop-blur-xl px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => nav(user?.role === "owner" ? "/dashboard" : "/home")} data-testid="brand-logo">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#EAB308,#CA8A04)" }}>
            <Gem className="h-5 w-5 text-[#0B0E14]" />
          </div>
          <div className="leading-tight">
            <div className="font-head font-bold text-lg tracking-tight">NNX<span className="text-[#EAB308]">Changer</span></div>
            <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Treasury System</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {shift && (
            <div className="hidden sm:flex items-center gap-1.5 text-xs mono px-2.5 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/40">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> {shift.shift_code}
            </div>
          )}
          <div className="text-right hidden sm:block">
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#EAB308]">{user?.role}</div>
          </div>
          {user?.role === "owner" && (
            <button onClick={() => nav("/approvals")} data-testid="approval-bell" title="Approval tertunda"
              className="relative p-2 rounded-lg border border-[#222B3D] hover:border-[#EAB308] hover:text-[#EAB308] transition-colors">
              <Bell className={`h-4 w-4 ${pending > 0 ? "text-[#EAB308]" : ""}`} />
              {pending > 0 && (
                <span data-testid="approval-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                  {pending}
                </span>
              )}
            </button>
          )}
          <button onClick={() => setShowPwd(true)} data-testid="change-password-btn" title="Ganti Password" className="p-2 rounded-lg border border-[#222B3D] hover:border-[#EAB308] hover:text-[#EAB308] transition-colors">
            <KeyRound className="h-4 w-4" />
          </button>
          <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-lg border border-[#222B3D] hover:border-rose-500/50 hover:text-rose-400 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:flex flex-col w-56 border-r border-[#222B3D] bg-[#0B0E14] p-3 gap-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={`nav-${it.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#EAB308] text-[#0B0E14]" : "text-[#94A3B8] hover:bg-[#151B28] hover:text-white"
                }`
              }>
              <it.icon className="h-4 w-4" /> {it.label}
            </NavLink>
          ))}
        </aside>

        {/* Main */}
        <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-6 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>

      <Assistant />
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}

      {/* Bottom nav (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 border-t border-[#222B3D] bg-[#0B0E14]/95 backdrop-blur-xl grid" style={{ gridTemplateColumns: `repeat(${mobileItems.length},1fr)` }}>
        {mobileItems.map((it) => (
          <NavLink key={it.to} to={it.to} data-testid={`mnav-${it.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition-colors ${
                isActive ? "text-[#EAB308]" : "text-[#64748B]"
              }`
            }>
            <it.icon className="h-5 w-5" /> {it.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
